import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';

import { env } from '@/env';
import { getQduelsAccessToken } from '@/lib/logto';
import type {
  ChallengeDTO,
  CreateChallengeRequest,
  CrProfileDTO,
  ListChallengesQuery,
  MatchDTO,
  MatchStatsDTO,
  PlayerByCrTagDTO,
  PlayerLinkDTO,
  QueueEnterRequest,
  QueueEntry,
  QueueStatus,
  VerifyTokenRequest,
  VerifyTokenResponse,
} from '@/types/qduels';

/**
 * QDuels API client. Talks to qduels-api at `/v1/*`.
 *
 * Auth: Logto Bearer JWT with audience `https://qduels.qlash.gg/api`, minted
 * by getQduelsAccessToken() through the shared serialized refresh-token
 * redemption (same pattern as qriots/qbot/qsocials). qduels-api validates the
 * JWT `aud` against exactly that audience and the issuer against
 * `https://account.qlash.gg/oidc`.
 *
 * ⚠️ Logto app grant required: minting a token for the qduels audience only
 * works if the TMA's Logto application has the "Qlash API QDuels" API resource
 * granted server-side (Logto console → Applications → <TMA app> → API
 * resources). If that grant is missing, getQduelsAccessToken() resolves to
 * null (refresh grant → HTTP 400), no Authorization header is attached, and
 * qduels answers 401 `missing_bearer_token`. This is the one config step the
 * Mini App can't self-provision — see the report / CLAUDE.md note.
 *
 * Dev: env.qduelsApiUrl = '/qduels-api', Vite proxy forwards to qduels.qlash.gg.
 * Prod: env.qduelsApiUrl = 'https://qduels.qlash.gg'.
 *
 * Unlike qbot/qsocials, qduels responses are NOT wrapped in a `{ data }` /
 * `{ success }` envelope — `POST /v1/challenges` returns the challenge object
 * directly with HTTP 201, and errors return `{ error: "<code>" }`.
 */
export const qduelsApi = createApi({
  reducerPath: 'qduelsApi',
  baseQuery: retry(
    fetchBaseQuery({
      baseUrl: env.qduelsApiUrl,
      prepareHeaders: async (headers) => {
        const token = await getQduelsAccessToken();
        if (token) headers.set('Authorization', `Bearer ${token}`);
        headers.set('Accept', 'application/json');
        headers.set('Content-Type', 'application/json');
        return headers;
      },
    }),
    {
      // RTK Query's RetryOptions is a discriminated union: pass EITHER
      // `maxRetries` OR `retryCondition`, never both (passing both makes TS
      // resolve to the `maxRetries` arm where `retryCondition` must be
      // `undefined`). We need conditional retries, so we use `retryCondition`
      // alone and cap the attempt count inside it via the `attempt` arg.
      //
      // Don't retry 4xx — a challenge that fails with defender_not_linked /
      // cannot_challenge_self / 401 is deterministic; retrying just delays the
      // error toast. Only transient network/5xx errors are worth ONE retry
      // (attempt === 1 is the first failure; bail on attempt >= 2).
      retryCondition: (error, _args, { attempt }) => {
        if (attempt > 1) return false;
        const status = (error as { status?: number | string }).status;
        if (status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR') return true;
        return typeof status === 'number' && status >= 500;
      },
    },
  ),
  // 'PlayerLink' tracks the caller's CR account link (GET /v1/players/me).
  // A successful verify-token invalidates it so getMyLink refetches and any
  // gated "Sfida" buttons flip from "link first" to enabled without a reload.
  // 'Challenges' tracks the caller's challenge lists; create/accept/decline/
  // cancel invalidate it so the Sfide tab re-fetches.
  // 'Queue' tracks the caller's matchmaking-queue status; enter/leave invalidate
  // it so a status refetch reflects the new in-queue state immediately.
  tagTypes: ['PlayerLink', 'Challenges', 'Queue'],
  endpoints: (builder) => ({
    /**
     * Create a 1v1 duel challenge against a friend. The challenger is derived
     * from the Bearer JWT server-side; the body carries the defender's Logto
     * id + match settings. Returns the created challenge (status `pending`).
     */
    createChallenge: builder.mutation<ChallengeDTO, CreateChallengeRequest>({
      query: (body) => ({
        url: '/v1/challenges',
        method: 'POST',
        body,
      }),
      // Refresh the caller's challenge lists so the new outgoing challenge
      // shows up in the Sfide tab immediately.
      invalidatesTags: ['Challenges'],
    }),

    /**
     * List the caller's challenges. `role: 'defender'` = incoming, `role:
     * 'challenger'` = outgoing; omit `role` to get both sides. Optional
     * `status` filter + `limit` (server caps at 100). Returns a bare array
     * (no envelope), newest-updated first.
     */
    listChallenges: builder.query<ChallengeDTO[], ListChallengesQuery | void>({
      query: (params) => ({
        url: '/v1/challenges',
        params: params
          ? {
              ...(params.role ? { role: params.role } : {}),
              ...(params.status ? { status: params.status } : {}),
              ...(params.limit != null ? { limit: params.limit } : {}),
            }
          : undefined,
      }),
      providesTags: ['Challenges'],
    }),

    /** Single challenge by id (GET /v1/challenges/:id). Used to poll status. */
    getChallenge: builder.query<ChallengeDTO, string>({
      query: (challengeId) => `/v1/challenges/${encodeURIComponent(challengeId)}`,
      providesTags: ['Challenges'],
    }),

    /**
     * Defender accepts a pending challenge. On success qduels flips it to
     * `in_progress` and creates the hosted Supercell match — both players then
     * receive the in-game invite. 403 `not_the_defender`, 409
     * `cannot_accept_<status>` on a stale/expired challenge.
     */
    acceptChallenge: builder.mutation<ChallengeDTO, string>({
      query: (challengeId) => ({
        url: `/v1/challenges/${encodeURIComponent(challengeId)}/accept`,
        method: 'POST',
      }),
      invalidatesTags: ['Challenges'],
    }),

    /** Defender declines a pending challenge (notifies the challenger). */
    declineChallenge: builder.mutation<ChallengeDTO, string>({
      query: (challengeId) => ({
        url: `/v1/challenges/${encodeURIComponent(challengeId)}/decline`,
        method: 'POST',
      }),
      invalidatesTags: ['Challenges'],
    }),

    /**
     * Challenger cancels their own challenge. While `pending` it's a plain
     * cancel; mid-series it counts as a forfeit (defender wins). 403
     * `not_the_challenger`.
     */
    cancelChallenge: builder.mutation<ChallengeDTO, string>({
      query: (challengeId) => ({
        url: `/v1/challenges/${encodeURIComponent(challengeId)}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: ['Challenges'],
    }),

    /**
     * The caller's current Clash Royale link (GET /v1/players/me).
     *
     * 404 `{ error: "no_linked_player" }` is the "not linked" state, NOT a
     * hard failure. RTK Query surfaces any non-2xx as `error`, so callers must
     * read `error.status === 404` as "not linked yet" (see `isNotLinkedError`
     * in qduelsErrors). We deliberately do NOT swallow the 404 into `data`
     * here: keeping it on the error channel lets the gate distinguish "not
     * linked" (404) from a transient auth/network failure that should not
     * silently behave like "not linked".
     */
    getMyLink: builder.query<PlayerLinkDTO, void>({
      query: () => '/v1/players/me',
      providesTags: ['PlayerLink'],
    }),

    /**
     * Link a Clash Royale account by verifying ownership via the in-game API
     * token (POST /v1/players/verify-token). On `verified: true` the caller's
     * logtoUserId is linked to `playerTag`; we invalidate 'PlayerLink' so the
     * link status refetches everywhere.
     *
     * Error cases the caller maps to copy: 422 `{ verified: false }` (token
     * wrong/expired), 409 `player_tag_already_linked`, 400 `invalid_player_tag`.
     */
    verifyToken: builder.mutation<VerifyTokenResponse, VerifyTokenRequest>({
      query: (body) => ({
        url: '/v1/players/verify-token',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PlayerLink'],
    }),

    /**
     * Cached Clash Royale profile for a tag (GET /v1/players/:playerTag/profile).
     * Optional — used only to show the linked account's name + headline stats.
     */
    playerProfile: builder.query<CrProfileDTO, string>({
      query: (playerTag) =>
        `/v1/players/${encodeURIComponent(playerTag)}/profile`,
      keepUnusedDataFor: 600,
    }),

    /**
     * Identity bridge (CR side): resolve a Clash Royale tag → the owner's
     * Logto `sub` (GET /v1/players/by-cr-tag/:tag). Lets CR surfaces (battle
     * opponent, public CR profile) light up follow/add-friend buttons for
     * opponents who linked their CR account in qduels.
     *
     * 404 `{ error: "player_not_found" }` (tag not linked) and 400
     * `{ error: "invalid_player_tag" }` are NOT hard failures — callers read
     * `error.status === 404`/`400` as "not resolved" and render no social
     * buttons (just the existing "View CR profile" affordance). The 4xx stays
     * on the error channel; the retry baseQuery already skips retrying 4xx.
     */
    getPlayerByCrTag: builder.query<PlayerByCrTagDTO, string>({
      query: (tag) => `/v1/players/by-cr-tag/${encodeURIComponent(tag)}`,
      keepUnusedDataFor: 600,
    }),

    // ─── Open matchmaking queue ──────────────────────────────────────────

    /**
     * Join the open 1v1 matchmaking queue (POST /v1/queue/enter). The caller is
     * derived from the Bearer JWT; the body carries the fixed pool. v1 always
     * sends `gameMode: 'REGULAR'` + `format: 'bo1'` (single fixed pool, single
     * game — no picker). Returns the created/updated `QueueEntry` (HTTP 201).
     * Re-entering while already queued is idempotent server-side.
     *
     * Errors mapped to UI copy via classifyQduelsQueueError: `player_not_linked`
     * (400) → /duels/link, `not_eligible` (403) → eligibility message,
     * `already_in_match` (409) → open Clash Royale.
     */
    queueEnter: builder.mutation<QueueEntry, QueueEnterRequest>({
      query: (body) => ({
        url: '/v1/queue/enter',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Queue'],
    }),

    /**
     * Leave the matchmaking queue (POST /v1/queue/leave) → `{ left }`. Called on
     * the QueueSheet's close / cancel / timeout to avoid ghost queue entries
     * (the backend has no TTL/heartbeat yet — see the spec's backend follow-up).
     */
    queueLeave: builder.mutation<{ left: boolean }, void>({
      query: () => ({
        url: '/v1/queue/leave',
        method: 'POST',
      }),
      invalidatesTags: ['Queue'],
    }),

    /**
     * The caller's live matchmaking state (GET /v1/queue/status). Consumed with
     * `pollingInterval ≈ 3000ms` ONLY while the QueueSheet is open + searching;
     * the sheet flips to "found" when `currentMatch != null`.
     */
    queueStatus: builder.query<QueueStatus, void>({
      query: () => '/v1/queue/status',
      providesTags: ['Queue'],
    }),

    // ─── Match detail + post-match stats ─────────────────────────────────

    /**
     * A single match by its CR game tag (GET /v1/matches/:gameTag). Drives the
     * DuelResult header (winner / score / mode). Returned unwrapped. A 404
     * `{ error: "match_not_found" }` means the tag isn't known (yet).
     */
    getMatch: builder.query<MatchDTO, string>({
      query: (gameTag) => `/v1/matches/${encodeURIComponent(gameTag)}`,
      keepUnusedDataFor: 300,
    }),

    /**
     * Post-match stats + deck deep links for a completed match
     * (GET /v1/matches/:gameTag/stats). Drives the DuelResult per-player blocks
     * (decks + rich stats), attributed to you vs opponent by the `tag` each
     * `stats`/`copyDecks` entry carries. A 404 `{ error: "stats_not_found" }`
     * means the match has no computed stats (not complete / replay missing).
     */
    getMatchStats: builder.query<MatchStatsDTO, string>({
      query: (gameTag) => `/v1/matches/${encodeURIComponent(gameTag)}/stats`,
      keepUnusedDataFor: 300,
    }),
  }),
});

export const {
  useCreateChallengeMutation,
  useListChallengesQuery,
  useGetChallengeQuery,
  useAcceptChallengeMutation,
  useDeclineChallengeMutation,
  useCancelChallengeMutation,
  useGetMyLinkQuery,
  useVerifyTokenMutation,
  usePlayerProfileQuery,
  useGetPlayerByCrTagQuery,
  useQueueEnterMutation,
  useQueueLeaveMutation,
  useQueueStatusQuery,
  useGetMatchQuery,
  useGetMatchStatsQuery,
} = qduelsApi;
